import { handleApiError, handleApiResponse } from "@/methods/api";
import ApiVerified from "@/services/apiVerified";
import {
  SchoolConnectDisplay,
  SchoolConnectInputs,
  BatchSubjectTeacherDisplay,
  OfflineTestSubjectwiseReportDisplay,
  OfflineTestSubjectwiseReportInputs,
  SchoolAcademicPlan,
  SchoolAcademicPlanMonthly,
  SchoolAcademicPlanMonthlyInputs,
  SchoolInfrastructure,
  SchoolMonthlyMeetingDisplay,
  SchoolMonthlyMeetingInputs,
  SchoolMonthlyMeetingUpdate,
  SchoolProfileDisplay,
  SchoolProfileInputs,
  SchoolQuery,
  SchoolUser,
  SchoolUserDisplay,
  SchoolUsersDisplay,
  SchoolUserUpdate,
  UpdateSchoolProfile,
} from "@/types/admin";
import { AffiliationCourseBatch, PdfReportDisplay } from "@/types/affiliation";
import { ApiResponse, IdNamePair } from "@/types/global";

export default class SchoolProfileApi extends ApiVerified {
  async saveSchoolQuery(schoolQuery: SchoolQuery): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `/school-profile/save-school-query`,
        schoolQuery,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async updateSchoolQuery(schoolQuery: SchoolQuery): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `/school-profile/update-school-query`,
        schoolQuery,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getSchoolQueriesForSchool(schoolProfileId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<SchoolQuery>>(
        `school-profile/get-school-queries-for-school`,
        { params: { schoolProfileId: schoolProfileId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getSchoolQueriesForOwner(ownerId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<SchoolQuery>>(
        `school-profile/get-school-queries-for-owner`,
        { params: { ownerId: ownerId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getSchoolConnectsForUser(userId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<SchoolConnectDisplay>>(
        `school-profile/get-school-connects-for-user`,
        { params: { userId: userId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getSchoolConnectsForSchool(schoolProfileId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<SchoolConnectDisplay>>(
        `school-profile/get-school-connects-for-school`,
        { params: { schoolProfileId: schoolProfileId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async saveSchoolConnect(schoolConnect: SchoolConnectInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `/school-profile/save-school-connect`,
        schoolConnect,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async updateSchoolConnect(schoolConnect: SchoolConnectInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `/school-profile/update-school-connect`,
        schoolConnect,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getSchoolsForAem(userId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<IdNamePair>>(
        `school-profile/get-schools-for-aem`,
        {
          params: { userId: userId },
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getSchoolUsersForMapping(onlyActive: boolean): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<{
        [schoolUserRoleId: number]: Array<IdNamePair>;
      }>(`school-profile/get-school-users-for-mapping`, { params: { onlyActive: onlyActive } });
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getSchoolUsersByRole(schoolUserRoleId: number, onlyActive: boolean): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<IdNamePair>>(
        `school-profile/get-school-users-by-role`,
        { params: { schoolUserRoleId: schoolUserRoleId, onlyActive: onlyActive } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async addSchoolAcademicPlan(schoolAcademicPlan: SchoolAcademicPlan): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `school-profile/add-school-academic-plan`,
        schoolAcademicPlan,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async addSchoolProfile(schoolProfileInputs: SchoolProfileInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `school-profile/add-school-profile`,
        schoolProfileInputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async addSchoolAcademicPlanMonthly(
    schoolAcademicPlanMonthlyInputs: SchoolAcademicPlanMonthlyInputs,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `school-profile/add-school-academic-plan-monthly`,
        schoolAcademicPlanMonthlyInputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async addSchoolUser(schoolUserInputs: SchoolUser): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `school-profile/add-school-user`,
        schoolUserInputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async registerInternalSchoolUser(userId: number, schoolUserRoleId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `school-profile/register-internal-school-user`,
        {
          userId: userId,
          schoolUserRoleId: schoolUserRoleId,
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getSchoolProfilesForAem(userId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<SchoolProfileDisplay>>(
        `school-profile/get-school-profiles-for-aem`,
        { params: { userId: userId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getSchoolProfileForSchool(schoolProfileId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<SchoolProfileDisplay>(
        `school-profile/get-school-profile-for-school`,
        { params: { schoolProfileId: schoolProfileId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getSchoolAcademicPlan(schoolProfileId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<SchoolAcademicPlan>(
        `school-profile/get-school-academic-plan`,
        {
          params: { schoolProfileId: schoolProfileId },
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getSchoolUserByEmail(emailId: string, schoolUserRoleId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<IdNamePair>(
        `school-profile/get-school-user-by-email`,
        { params: { emailId: emailId, schoolUserRoleId: schoolUserRoleId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async updateSchoolProfile(updateSchoolProfile: UpdateSchoolProfile): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `school-profile/update-school-profile`,
        updateSchoolProfile,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getDetailedSchoolUsers(userId: number | null): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<SchoolUserDisplay>>(
        `school-profile/get-detailed-school-users`,
        { params: { userId: userId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async updateSchoolUser(updateSchoolUserInputs: SchoolUserUpdate): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `school-profile/update-school-user`,
        updateSchoolUserInputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async addSchoolInfrastucture(
    schoolProfileId: number,
    schoolInfrastructure: SchoolInfrastructure,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `school-profile/add-school-infrastructure`,
        {
          schoolProfileId: schoolProfileId,
          schoolInfrastructure: schoolInfrastructure,
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getSchoolAcademicPlanMonthly(
    schoolProfileId: number,
    startMonth: string,
    endMonth: string,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<SchoolAcademicPlanMonthly>>(
        `school-profile/get-school-academic-plan-monthly`,
        {
          params: { schoolProfileId: schoolProfileId, startMonth: startMonth, endMonth: endMonth },
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getAffiliationsForAem(userId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<IdNamePair>>(
        `school-profile/get-affiliations-for-aem`,
        { params: { userId: userId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getCourseBatchTreeForAem(userId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<AffiliationCourseBatch>>(
        `school-profile/get-course-batch-tree-for-aem`,
        { params: { userId: userId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getUsersByRoleId(roleId: number, onlyActive: boolean): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<IdNamePair>>(`user/get-users-by-roleId`, {
        params: { roleId: roleId, onlyActive: onlyActive },
      });
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async mapSchoolToAffiliation(
    schoolProfileId: number,
    affiliationId: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.put<boolean>(
        `school-profile/map-school-to-affiliation`,
        {
          schoolProfileId: schoolProfileId,
          affiliationId: affiliationId,
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async scheduleSchoolMonthlyMeeting(
    schoolMonthlyMeetings: SchoolMonthlyMeetingInputs,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `school-profile/schedule-school-monthly-meeting`,
        schoolMonthlyMeetings,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getSchoolMonthlyMeetingsForAem(
    userId: number,
    meetingCategoryId: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<SchoolMonthlyMeetingDisplay>>(
        `school-profile/get-school-monthly-meetings-for-aem`,
        { params: { userId: userId, meetingCategoryId: meetingCategoryId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getSchoolMonthlyMeetingsBySchool(schoolProfileId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<SchoolMonthlyMeetingDisplay>>(
        `school-profile/get-school-monthly-meetings-by-school`,
        { params: { schoolProfileId: schoolProfileId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getSchoolUsersForSchool(schoolProfileId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<SchoolUsersDisplay>(
        `school-profile/get-school-users-for-school`,
        { params: { schoolProfileId: schoolProfileId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async updateSchoolMonthlyMeeting(
    updateSchoolMeetings: SchoolMonthlyMeetingUpdate,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.put<boolean>(
        `school-profile/update-school-monthly-meeting`,
        updateSchoolMeetings,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async rescheduleSchoolMonthlyMeeting(meetingDatetimeStr: string): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.put<boolean>(
        `school-profile/reschedule-school-monthly-meeting`,
        meetingDatetimeStr,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async updateSchoolMonthlyMeetingStatus(meetingStatusId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.put<boolean>(
        `school-profile/update-school-monthly-meeting-status`,
        meetingStatusId,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async submitOfflineTestSubjectWiseReport(
    reportInputs: OfflineTestSubjectwiseReportInputs,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `pdf-report/submit-offline-test-subject-wise-report`,
        reportInputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getOfflineTestSubjectWiseReportByBatch(batchId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<OfflineTestSubjectwiseReportDisplay>>(
        `pdf-report/get-offline-test-subject-wise-reports-by-batch`,
        { params: { batchId: batchId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getPdfReportsForSchool(schoolProfileId: number, reportType: string): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<PdfReportDisplay>>(
        `school-profile/get-pdf-reports-for-school`,
        { params: { schoolProfileId: schoolProfileId, reportType: reportType } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async generatePdfReport(reportType: string, reportId: string): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(`pdf-report/generate-pdf-report`, {
        reportType: reportType,
        reportId: reportId,
      });
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getBatchTeacherMappingsForSchool(schoolProfileId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<BatchSubjectTeacherDisplay>>(
        `school-profile/get-batch-teacher-mappings-for-school`,
        { params: { schoolProfileId: schoolProfileId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getTeachersForSchool(schoolProfileId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<IdNamePair>>(
        `school-profile/get-teachers-for-school`,
        { params: { schoolProfileId: schoolProfileId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async mapTeacherToBatchSubject(
    batchId: number,
    subjectId: number,
    userId: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `school-profile/map-teacher-to-batch-subject`,
        { BatchId: batchId, SubjectId: subjectId, UserId: userId },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async bulkMapTeachersToBatches(schoolProfileId: number, csvFile: string): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `school-profile/bulk-map-teachers-to-batches`,
        { schoolProfileId: schoolProfileId, csvFile: csvFile },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }
}
